import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { fmtUSD, bucketBarColor, type AgingBucketRowView } from '../../../utils/outstandingUtils';

interface AgingBucketChartProps {
  buckets: AgingBucketRowView[];
  selectedRowKey: string | null;
  onSelectRowKey: (rowKey: string) => void;
  /** Omit outer card + title when nested in a parent section (e.g. chart/table toggle). */
  embedded?: boolean;
}

const AgingTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  label?: string;
}> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload as AgingBucketRowView;
  if (!d || d.chartXLabel == null) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[200px]">
      <p className="text-[11px] font-bold text-slate-700 mb-2">{d.chartXLabel}</p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] text-slate-500">Outstanding</span>
          <span className="text-[12px] font-bold text-slate-800 tabular-nums">{fmtUSD(d.total_outstanding ?? 0)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] text-slate-500">% of Total</span>
          <span className="text-[12px] font-semibold text-slate-700 tabular-nums">{(d.percentage_of_total ?? 0).toFixed(1)}%</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] text-slate-500">Invoices</span>
          <span className="text-[12px] font-semibold text-slate-700 tabular-nums">{d.invoice_count ?? 0}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] text-slate-500">Clients</span>
          <span className="text-[12px] font-semibold text-slate-700 tabular-nums">{d.unique_clients ?? 0}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-[11px] text-slate-500">Risk</span>
          <span className="text-[11px] font-semibold text-slate-700">{d.collection_risk ?? 'N/A'}</span>
        </div>
      </div>
      <p className="text-[10px] text-blue-600 mt-2 font-medium">Click bar to view clients</p>
    </div>
  );
};

export const AgingBucketChart: React.FC<AgingBucketChartProps> = ({
  buckets,
  selectedRowKey,
  onSelectRowKey,
  embedded = false,
}) => {
  const body = (
    <>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart
          data={buckets}
          margin={{ top: 8, right: 16, left: 8, bottom: 32 }}
          onClick={e => {
            if (e?.activePayload?.[0]) {
              const b = e.activePayload[0].payload as AgingBucketRowView;
              onSelectRowKey(b.rowKey === selectedRowKey ? '' : b.rowKey);
            }
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
          <XAxis
            dataKey="chartXLabel"
            tick={{ fontSize: 9, fill: '#64748B' }}
            axisLine={false}
            tickLine={false}
            interval={0}
            angle={-28}
            textAnchor="end"
            height={56}
          />
          <YAxis
            tickFormatter={v => fmtUSD(v)}
            tick={{ fontSize: 10, fill: '#94A3B8' }}
            axisLine={false}
            tickLine={false}
            width={60}
          />
          <Tooltip content={<AgingTooltip />} cursor={{ fill: '#F8FAFC' }} />
          <Bar dataKey="total_outstanding" radius={[4, 4, 0, 0]} style={{ cursor: 'pointer' }}>
            {buckets.map((bucket, i) => (
              <Cell
                key={bucket.rowKey}
                fill={bucketBarColor(bucket)}
                opacity={selectedRowKey && selectedRowKey !== bucket.rowKey ? 0.4 : 1}
                stroke={selectedRowKey === bucket.rowKey ? '#1E293B' : 'none'}
                strokeWidth={selectedRowKey === bucket.rowKey ? 1.5 : 0}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 justify-center">
        {[
          { color: '#3B82F6', label: 'Current' },
          { color: '#10B981', label: '1-30 days' },
          { color: '#F59E0B', label: '31-60 / 61-90 days' },
          { color: '#EF4444', label: '90+ days' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5 text-[10px] text-slate-600">
            <div className="w-2.5 h-2.5 rounded-sm flex-none" style={{ backgroundColor: color }} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </>
  );

  if (embedded) {
    return <>{body}</>;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100">
        <div className="w-0.5 h-4 rounded-full bg-red-500 flex-none" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
          Outstanding by Aging Period
        </span>
        <span className="text-[10px] text-slate-400 ml-auto">Click a bar to view client details</span>
      </div>
      <div className="p-4">{body}</div>
    </div>
  );
};

export default AgingBucketChart;
